import { Link, useParams } from 'react-router-dom';
import {
  ArrowPathIcon,
  QuestionMarkCircleIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  SparklesIcon,
  LightBulbIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  DocumentTextIcon,
  BoltIcon,
  ArrowRightIcon,
  ChartBarIcon,
  CodeBracketIcon,
  TableCellsIcon,
  ClockIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';
import Navbar from '../../components/Navbar';
import Chatbot from '../../components/Chatbot';

const bestPractices = [
  {
    id: 'avoid-hardcoded-ids',
    title: 'Avoid Hardcoded IDs',
    description: 'Build flexible, environment-independent flows',
    icon: ExclamationTriangleIcon
  },
  {
    id: 'user-assignment',
    title: 'Dynamic User Assignment',
    description: 'Use queues and dynamic lookups instead of hardcoded user IDs',
    icon: UserGroupIcon
  },
  {
    id: 'record-type-assignment',
    title: 'Record Type Best Practices',
    description: 'Query record types by DeveloperName for reliability',
    icon: DocumentTextIcon
  },
  {
    id: 'custom-metadata',
    title: 'Using Custom Metadata Types',
    description: 'Store configuration values for flexible deployment',
    icon: Cog6ToothIcon
  },
  {
    id: 'entry-criteria',
    title: 'Optimizing Entry Criteria',
    description: 'Improve flow performance and reduce unnecessary executions',
    icon: BoltIcon
  }
];

export default function HelpPage() {
  const { orgId } = useParams();
  const basePath = orgId ? `/${orgId}` : '';
  
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Help Center
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Find answers to your questions and learn how to get the most out of FlowDocs
          </p>
        </div>

        {/* Quick Start - Featured Section */}
        <div className="mb-8">
          <Link
            to={`${basePath}/help/quick-start`}
            className="block bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all transform hover:scale-[1.01]"
          >
            <div className="p-8 flex items-center justify-between">
              <div className="flex items-center space-x-6">
                <div className="flex-shrink-0">
                  <div className="h-16 w-16 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                    <RocketLaunchIcon className="h-9 w-9 text-white" />
                  </div>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">
                    New to FlowDocs? Start Here!
                  </h2>
                  <p className="text-blue-100 text-lg">
                    Follow our 3-step Quick Start Guide and be up and running in 10 minutes
                  </p>
                  <div className="flex items-center space-x-4 mt-3 text-sm text-blue-100">
                    <div className="flex items-center">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      <span>~10 minutes</span>
                    </div>
                    <span>•</span>
                    <div className="flex items-center">
                      <span>3 easy steps</span>
                    </div>
                    <span>•</span>
                    <div className="flex items-center">
                      <span>No credit card required</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="hidden md:block">
                <div className="flex items-center text-white">
                  <span className="text-lg font-semibold mr-2">Get Started</span>
                  <ArrowRightIcon className="h-6 w-6" />
                </div>
              </div>
            </div>
          </Link>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Help Topics (2/3 width) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Documentation Section */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Documentation</h2>
              <div className="space-y-4">
                <Link
                  to={`${basePath}/help/sync-methods`}
                  className="block p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-sm transition-all"
                >
                  <div className="flex items-start">
                    <ArrowPathIcon className="h-6 w-6 text-blue-600 mt-0.5" />
                    <div className="ml-3">
                      <h3 className="font-medium text-gray-900">Sync Methods</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Learn about the different ways FlowDocs syncs your Salesforce flows
                      </p>
                    </div>
                  </div>
                </Link>
              </div>
            </div>

            {/* Feature Guides Section */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Feature Guides</h2>
              <div className="grid gap-3">
                <Link
                  to={`${basePath}/help/dashboard-guide`}
                  className="flex items-center p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-sm transition-all"
                >
                  <ChartBarIcon className="h-5 w-5 text-gray-600 mr-3" />
                  <div>
                    <h4 className="font-medium text-gray-900">Dashboard Guide</h4>
                    <p className="text-sm text-gray-600">Monitor flow health and sync error data from Salesforce</p>
                  </div>
                </Link>

                <Link
                  to={`${basePath}/help/orgs-guide`}
                  className="flex items-center p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-sm transition-all"
                >
                  <Cog6ToothIcon className="h-5 w-5 text-gray-600 mr-3" />
                  <div>
                    <h4 className="font-medium text-gray-900">Orgs Guide</h4>
                    <p className="text-sm text-gray-600">Connect Salesforce orgs, manage environments, and sync flows</p>
                  </div>
                </Link>

                <Link
                  to={`${basePath}/help/flows-guide`}
                  className="flex items-center p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-sm transition-all"
                >
                  <BoltIcon className="h-5 w-5 text-gray-600 mr-3" />
                  <div>
                    <h4 className="font-medium text-gray-900">Flows Guide</h4>
                    <p className="text-sm text-gray-600">Search, filter, and analyze flow performance grades</p>
                  </div>
                </Link>

                <Link
                  to={`${basePath}/help/fields-guide`}
                  className="flex items-center p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-sm transition-all"
                >
                  <TableCellsIcon className="h-5 w-5 text-gray-600 mr-3" />
                  <div>
                    <h4 className="font-medium text-gray-900">Fields Guide</h4>
                    <p className="text-sm text-gray-600">See which flows use specific fields in your objects</p>
                  </div>
                </Link>

                <Link
                  to={`${basePath}/help/entry-criteria-guide`}
                  className="flex items-center p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-sm transition-all"
                >
                  <CodeBracketIcon className="h-5 w-5 text-gray-600 mr-3" />
                  <div>
                    <h4 className="font-medium text-gray-900">Entry Criteria Guide</h4>
                    <p className="text-sm text-gray-600">Analyze record-triggered flows and identify entry criteria conflicts</p>
                  </div>
                </Link>
              </div>
            </div>

          </div>

          {/* Right Column - Best Practices (1/3 width) */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg shadow-sm p-6 border border-yellow-200">
              <div className="flex items-center mb-4">
                <LightBulbIcon className="h-8 w-8 text-yellow-600" />
                <h2 className="text-xl font-semibold text-gray-900 ml-2">Best Practices</h2>
              </div>
              <p className="text-sm text-gray-700 mb-4">
                Learn proven techniques for building maintainable and scalable Salesforce flows
              </p>
              <div className="space-y-3">
                {bestPractices.map((practice) => {
                  const Icon = practice.icon;
                  return (
                    <Link
                      key={practice.id}
                      to={`${basePath}/help/best-practices/${practice.id}`}
                      className="block p-3 bg-white bg-opacity-70 rounded-lg hover:bg-opacity-100 transition-all group"
                    >
                      <div className="flex items-start">
                        <Icon className="h-5 w-5 text-gray-600 mt-0.5 group-hover:text-yellow-600 transition-colors" />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-yellow-700">
                            {practice.title}
                          </h4>
                          <p className="text-xs text-gray-600 mt-0.5">
                            {practice.description}
                          </p>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
              <Link
                to={`${basePath}/help/best-practices`}
                className="mt-4 inline-flex items-center text-sm font-medium text-yellow-700 hover:text-yellow-800"
              >
                View all best practices
                <ArrowRightIcon className="ml-1 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Section - Getting Started and Support */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
          {/* Getting Started Section */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Getting Started</h2>
            <ul className="space-y-3">
              <li>
                <Link to="/connect-salesforce" className="text-blue-600 hover:text-blue-800 flex items-center">
                  <ArrowRightIcon className="h-4 w-4 mr-2" />
                  Connect your Salesforce org
                </Link>
              </li>
              <li>
                <Link to={`${basePath}/flows`} className="text-blue-600 hover:text-blue-800 flex items-center">
                  <ArrowRightIcon className="h-4 w-4 mr-2" />
                  View your flows
                </Link>
              </li>
              <li>
                <Link to={`${basePath}/dashboard`} className="text-blue-600 hover:text-blue-800 flex items-center">
                  <ArrowRightIcon className="h-4 w-4 mr-2" />
                  Explore your dashboard
                </Link>
              </li>
            </ul>
          </div>

          {/* Support Section */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Support</h2>
            <Link 
              to={`${basePath}/help/contact-support`}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 mb-4"
            >
              Contact Support
            </Link>
            <div className="mt-4 space-y-2 text-sm text-gray-600">
              <div className="flex items-start">
                <ClockIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-700">Support Hours</p>
                  <p>Monday - Friday, 9AM - 5PM PST</p>
                </div>
              </div>
              <div className="flex items-start">
                <ArrowPathIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-700">Response Time</p>
                  <p>Within 24 hours during business days</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Chatbot />
    </div>
  );
}